import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { profileService } from '../../services/profileService';
import { authService } from '../../services/authService';
import { UserWithProfile, ProfileUpdateData } from '../../types';
import { PricingModal } from '../../components/Pricing';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  UserCircleIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  KeyIcon,
  LockClosedIcon,
  XMarkIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { API_CONFIG, API_ENDPOINTS } from '../../config/api';
import { getAccessToken } from '../../utils/authStorage';
import { useAuth } from '../../contexts/AuthContext';

type TabType = 'profile' | 'password' | 'subscription' | 'usage';

const CANCELLATION_REASONS = [
  { id: 'not_using', label: 'Não estou usando o produto' },
  { id: 'too_expensive', label: 'Está muito caro' },
  { id: 'agents_dont_help', label: 'Os agentes não me atendem' },
  { id: 'had_problems', label: 'Tive vários problemas ao usar' },
  { id: 'other', label: 'Outro não listado' },
] as const;

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabType | null;
  
  const [activeTab, setActiveTab] = useState<TabType>(tabParam && ['profile', 'password', 'subscription', 'usage'].includes(tabParam) ? tabParam : 'profile');
  const [, setUser] = useState<UserWithProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingModalTitle, setPricingModalTitle] = useState<string>('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cancelModalStep, setCancelModalStep] = useState<1 | 2>(1);
  const [cancelReasons, setCancelReasons] = useState<Record<string, boolean>>({});
  const [cancelOtherText, setCancelOtherText] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string>('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Profile form state
  const [formData, setFormData] = useState<ProfileUpdateData>({
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    position: '',
    industry: '',
    address: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    timezone: 'America/Sao_Paulo',
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  });

  // Timezone options
  const timezones = [
    { value: 'America/Sao_Paulo', label: 'Brasília (GMT-3)' },
    { value: 'America/Manaus', label: 'Manaus (GMT-4)' },
    { value: 'America/Fortaleza', label: 'Fortaleza (GMT-3)' },
    { value: 'America/Recife', label: 'Recife (GMT-3)' },
    { value: 'America/Salvador', label: 'Salvador (GMT-3)' },
    { value: 'America/New_York', label: 'Nova York (GMT-5)' },
    { value: 'America/Chicago', label: 'Chicago (GMT-6)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
    { value: 'Europe/London', label: 'Londres (GMT+0)' },
    { value: 'Europe/Paris', label: 'Paris (GMT+1)' },
    { value: 'Asia/Tokyo', label: 'Tóquio (GMT+9)' },
    { value: 'UTC', label: 'UTC (Tempo Universal)' },
  ];

  useEffect(() => {
    loadProfile();
  }, []);

  const [usageData, setUsageData] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [showUsageWarningModal, setShowUsageWarningModal] = useState(false);
  const [hasShownUsageWarning, setHasShownUsageWarning] = useState(false);

  useEffect(() => {
    if (activeTab === 'subscription') {
      loadSubscription();
    }
    if (activeTab === 'usage') {
      loadUsageData();
    }
  }, [activeTab]);

  // Modal de upgrade: uso elevado (85% a 99%)
  useEffect(() => {
    const isUnlimited = usageData?.credits?.unlimited || usageData?.tokens?.unlimited;
    if (usageData && !hasShownUsageWarning && !isUnlimited) {
      const usagePercent = usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0;
      if (usagePercent >= 85 && usagePercent < 100) {
        setShowUsageWarningModal(true);
        setHasShownUsageWarning(true);
      }
    }
  }, [usageData, hasShownUsageWarning]);

  // Update activeTab when URL param changes
  useEffect(() => {
    const tabParam = searchParams.get('tab') as TabType | null;
    if (tabParam && ['profile', 'password', 'subscription', 'usage'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && success) {
        setSuccess('');
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [success]);

  const normalizePlanName = (name: string | undefined): string => {
    if (!name) return '';
    return name.replace(/Gratuíto/g, 'Gratuito').replace(/gratuíto/g, 'gratuito');
  };

  const getSubscriptionInterval = (sub: any): string => {
    const name = sub?.plan_name ?? sub?.plan_config?.plan_name ?? '';
    if (/-?\s*Anual$/i.test(name)) return 'Anual';
    if (/-?\s*Semestral$/i.test(name)) return 'Semestral';
    if (/-?\s*Mensal$/i.test(name)) return 'Mensal';
    return 'Mensal';
  };

  const openCancelModal = () => {
    setCancelModalStep(1);
    setCancelReasons({});
    setCancelOtherText('');
    setCancelError('');
    setShowCancelModal(true);
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const token = getAccessToken();
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.DELETE_ACCOUNT}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Erro ao deletar conta.');
        setShowDeleteModal(false);
        return;
      }
      setShowDeleteModal(false);
      logout();
      navigate('/');
    } catch {
      setError('Erro ao deletar conta. Tente novamente.');
      setShowDeleteModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getNextPlan = (currentPlan: string): { type: string; name: string } | null => {
    const planHierarchy: Record<string, { type: string; name: string } | null> = {
      'alef': { type: 'beit', name: 'Beit' },
      'freemium': { type: 'beit', name: 'Beit' },
      'beit': { type: 'guimel', name: 'Guimel' },
      'guimel': { type: 'dalet', name: 'Dalet' },
      'dalet': null,
      'intermediario': { type: 'avancado', name: 'Avançado' },
      'avancado': null,
    };
    return planHierarchy[currentPlan?.toLowerCase()] ?? null;
  };

  const getPrevPlan = (currentPlan: string): { type: string; name: string } | null => {
    const prevPlanHierarchy: Record<string, { type: string; name: string } | null> = {
      'beit': { type: 'alef', name: 'Alef' },
      'guimel': { type: 'beit', name: 'Beit' },
      'dalet': { type: 'guimel', name: 'Guimel' },
      'avancado': { type: 'intermediario', name: 'Intermediário' },
      'alef': null,
      'freemium': null,
      'intermediario': null,
    };
    return prevPlanHierarchy[currentPlan?.toLowerCase()] ?? null;
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.id) return;
    setCancelError('');
    setCancelLoading(true);
    try {
      const token = getAccessToken();
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.CANCEL_SUBSCRIPTION}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscription_id: subscription.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCancelError(data?.error || 'Erro ao cancelar assinatura.');
        return;
      }
      setSuccess('Assinatura cancelada com sucesso.');
      setShowCancelModal(false);
      loadSubscription();
    } catch {
      setCancelError('Erro ao cancelar assinatura. Tente novamente.');
    } finally {
      setCancelLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUBSCRIPTIONS}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Assinaturas ordenadas por active no backend; preferir a ativa
        const activeSubscription = data.subscriptions?.find((sub: any) => sub.active) ?? data.subscriptions?.[0];
        setSubscription(activeSubscription || null);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
      setSubscription(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const loadUsageData = async () => {
    try {
      setUsageLoading(true);
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.USAGE_SUMMARY}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsageData(data.usage || null);
      } else {
        setUsageData(null);
      }
    } catch (err) {
      console.error('Error loading usage data:', err);
      setUsageData(null);
    } finally {
      setUsageLoading(false);
    }
  };

  const formatCredits = (credits: number): string => {
    if (credits >= 1000000) {
      return `${(credits / 1000000).toFixed(1)}M`;
    } else if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}K`;
    }
    return credits.toFixed(0);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const profileData = await profileService.getProfile();
      setUser(profileData);
      
      // Populate form - ensure all fields are properly mapped and always strings
      const newFormData: ProfileUpdateData = {
        first_name: String(profileData.first_name || ''),
        last_name: String(profileData.last_name || ''),
        email: String(profileData.email || ''),
        company: String(profileData.profile?.company || ''),
        position: String(profileData.profile?.position || ''),
        industry: String(profileData.profile?.industry || ''),
        address: String(profileData.profile?.address || ''),
        phone: String(profileData.profile?.phone || ''),
        city: String(profileData.profile?.city || ''),
        state: String(profileData.profile?.state || ''),
        country: String(profileData.profile?.country || ''),
        postal_code: String(profileData.profile?.postal_code || ''),
        timezone: String(profileData.profile?.timezone || 'America/Sao_Paulo'),
      };
      
      setFormData(newFormData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  // Phone mask: 11 dígitos (DDD + 9 + 8) - formato XX 9XXXX-XXXX
  const formatPhone = (value: string): string => {
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.startsWith('55') && cleaned.length > 11) {
      cleaned = cleaned.substring(2);
    }
    cleaned = cleaned.slice(0, 11);
    if (cleaned.length <= 2) {
      return cleaned;
    }
    if (cleaned.length <= 7) {
      return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)}`;
    }
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Apply phone mask if it's the phone field
    let processedValue = value;
    if (name === 'phone') {
      processedValue = formatPhone(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Remove email from update data as it's not editable
      const { email, ...rest } = formData;
      const allowedKeys = ['first_name', 'last_name', 'company', 'address', 'phone', 'city', 'state', 'timezone'] as const;
      const cleanedData: Partial<ProfileUpdateData> = {};
      for (const key of allowedKeys) {
        if (key in rest) cleanedData[key] = rest[key] as string;
      }

      const updatedUser = await profileService.updateProfile(cleanedData);

      setUser(updatedUser);
      
      // Update form data with the response to ensure consistency - always use strings
      if (updatedUser.profile) {
        setFormData({
          phone: String(updatedUser.profile?.phone || ''),
          city: String(updatedUser.profile?.city || ''),
          state: String(updatedUser.profile?.state || ''),
          country: String(updatedUser.profile?.country || ''),
          postal_code: String(updatedUser.profile?.postal_code || ''),
          address: String(updatedUser.profile?.address || ''),
          company: String(updatedUser.profile?.company || ''),
          position: String(updatedUser.profile?.position || ''),
          industry: String(updatedUser.profile?.industry || ''),
          timezone: String(updatedUser.profile?.timezone || 'America/Sao_Paulo'),
          first_name: String(updatedUser.first_name || ''),
          last_name: String(updatedUser.last_name || ''),
          email: String(updatedUser.email || ''),
        });
      }
      
      setSuccess('Perfil atualizado com sucesso!');
    } catch (err) {
      console.error('Error updating profile:', err);
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Erro ao atualizar perfil. Por favor, tente novamente.';
      setError(errorMessage);
      
      // Keep error message visible for longer
      setTimeout(() => setError(''), 8000);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.new_password_confirm) {
      setError('As senhas não coincidem');
      setSaving(false);
      return;
    }

    try {
      await authService.changePassword(
        passwordData.old_password, 
        passwordData.new_password,
        passwordData.new_password_confirm
      );
      setSuccess('Senha alterada com sucesso!');
      setPasswordData({
        old_password: '',
        new_password: '',
        new_password_confirm: '',
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao alterar senha');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-foreground">Carregando perfil...</div>
      </div>
    );
  }

  const inputBaseClass = "w-full px-4 py-2 !bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all";
  const inputDisabledClass = "w-full px-4 py-2 !bg-background border border-border rounded-lg text-muted-foreground cursor-not-allowed";

  return (
    <div className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
      <div className="bg-card rounded-lg shadow-lg p-6 md:p-8 lg:p-10 w-full border border-border">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex items-center gap-3">
              <UserCircleIcon className="w-8 h-8 text-secondary" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                  Minha Conta
                </h1>
                <p className="text-muted-foreground mt-1">
                  Gerencie suas informações pessoais e preferências
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                navigate('/app/support-messages');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-card hover:bg-secondary/5 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors duration-150 whitespace-nowrap flex-shrink-0"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Central de Mensagens</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border-2 border-red-600 rounded-lg flex items-start gap-3 shadow-lg">
            <ExclamationCircleIcon className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-200 font-medium text-base">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-200 transition-colors"
              aria-label="Fechar mensagem de erro"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-card rounded-lg border border-border p-2 mb-6 overflow-x-auto">
          <nav className="flex space-x-2 min-w-max md:min-w-0" aria-label="Tabs">
            <button
              onClick={() => {
                setActiveTab('profile');
                setError('');
                setSuccess('');
                setSearchParams({});
              }}
              className={`
                flex-shrink-0 md:flex-1 py-3 px-4 font-medium text-sm transition-all duration-150 rounded-lg whitespace-nowrap
                ${
                  activeTab === 'profile'
                    ? 'bg-secondary/10 text-secondary border border-secondary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/5'
                }
              `}
            >
              <div className="flex items-center justify-center gap-2">
                <UserCircleIcon className="w-5 h-5" />
                Perfil
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('password');
                setError('');
                setSuccess('');
                setSearchParams({ tab: 'password' });
              }}
              className={`
                flex-shrink-0 md:flex-1 py-3 px-4 font-medium text-sm transition-all duration-150 rounded-lg whitespace-nowrap
                ${
                  activeTab === 'password'
                    ? 'bg-card text-secondary border border-secondary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
                }
              `}
            >
              <div className="flex items-center justify-center gap-2">
                <KeyIcon className="w-5 h-5" />
                Segurança
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('subscription');
                setError('');
                setSuccess('');
                setSearchParams({ tab: 'subscription' });
              }}
              className={`
                flex-shrink-0 md:flex-1 py-3 px-4 font-medium text-sm transition-all duration-150 rounded-lg whitespace-nowrap
                ${
                  activeTab === 'subscription'
                    ? 'bg-card text-secondary border border-secondary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
                }
              `}
            >
              <div className="flex items-center justify-center gap-2">
                <CreditCardIcon className="w-5 h-5" />
                Assinatura
              </div>
            </button>
            <button
              onClick={() => {
                setActiveTab('usage');
                setError('');
                setSuccess('');
                setSearchParams({ tab: 'usage' });
              }}
              className={`
                flex-shrink-0 md:flex-1 py-3 px-4 font-medium text-sm transition-all duration-150 rounded-lg whitespace-nowrap
                ${
                  activeTab === 'usage'
                    ? 'bg-card text-secondary border border-secondary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
                }
              `}
            >
              <div className="flex items-center justify-center gap-2">
                <ChartBarIcon className="w-5 h-5" />
                Uso
              </div>
            </button>
          </nav>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="space-y-6 w-full [&_input[type='text']]:!bg-background [&_input[type='email']]:!bg-background [&_select]:!bg-background">
            {/* Personal Information */}
            <section className="space-y-4 w-full">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <UserCircleIcon className="w-5 h-5 text-secondary" />
                Informações Pessoais
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-muted-foreground mb-1">
                    Primeiro Nome *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className={inputBaseClass}
                  />
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-muted-foreground mb-1">
                    Sobrenome *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className={inputBaseClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <EnvelopeIcon className="w-4 h-4" />
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  readOnly
                  disabled
                  className={inputDisabledClass}
                />
                <p className="mt-1 text-xs text-muted-foreground">O email não pode ser alterado</p>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-muted-foreground mb-1">
                  Igreja ou Denominação
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={inputBaseClass}
                />
              </div>
            </section>

            {/* Address Information */}
            <section className="space-y-4 w-full">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <MapPinIcon className="w-5 h-5 text-secondary" />
                Endereço
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-muted-foreground mb-1">
                    Endereço: Rua, número e complemento
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={inputBaseClass}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-muted-foreground mb-1">
                    Celular
                  </label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputBaseClass}
                    placeholder="11 99999-0000"
                    maxLength={15}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-muted-foreground mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={inputBaseClass}
                  />
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-muted-foreground mb-1">
                    Estado
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={inputBaseClass}
                  />
                </div>
              </div>
            </section>

            {/* Preferences */}
            <section className="space-y-4 w-full">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-secondary" />
                Preferências
              </h2>
              
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-muted-foreground mb-1">
                  Fuso Horário
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  className={inputBaseClass}
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            {/* Submit Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                type="submit"
                variant="hero"
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-6 w-full">
            <section className="space-y-4 w-full">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <LockClosedIcon className="w-5 h-5 text-secondary" />
                Alterar Senha
              </h2>
              
              <div className="w-full">
                <div className="mb-4">
                  <label htmlFor="old_password" className="block text-sm font-medium text-muted-foreground mb-1">
                    Senha Atual *
                  </label>
                  <input
                    type="password"
                    id="old_password"
                    name="old_password"
                    value={passwordData.old_password}
                    onChange={handlePasswordChange}
                    required
                    className={inputBaseClass}
                    placeholder="Digite sua senha atual"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="new_password" className="block text-sm font-medium text-muted-foreground mb-1">
                    Nova Senha *
                  </label>
                  <input
                    type="password"
                    id="new_password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                    className={inputBaseClass}
                    placeholder="Digite sua nova senha (mínimo 8 caracteres)"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="new_password_confirm" className="block text-sm font-medium text-muted-foreground mb-1">
                    Confirmar Nova Senha *
                  </label>
                  <input
                    type="password"
                    id="new_password_confirm"
                    name="new_password_confirm"
                    value={passwordData.new_password_confirm}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                    className={inputBaseClass}
                    placeholder="Confirme sua nova senha"
                  />
                </div>

                <div className="bg-card/30 border border-border rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>Requisitos da senha:</strong>
                  </p>
                  <ul className="text-xs text-muted-foreground mt-2 list-disc list-inside space-y-1">
                    <li>Mínimo de 8 caracteres</li>
                    <li>Não deve ser muito comum</li>
                    <li>Não deve conter apenas números</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Submit Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                type="submit"
                variant="hero"
                disabled={saving}
              >
                {saving ? 'Alterando...' : 'Alterar Senha'}
              </Button>
            </div>
          </form>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="space-y-6 w-full">
            <section className="space-y-4 w-full">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-secondary" />
                Uso de Créditos
              </h2>

              {usageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-foreground">Carregando dados de uso...</div>
                </div>
              ) : usageData ? (
                <div className="space-y-6">
                  {/* Credits Usage */}
                  <div className="bg-card rounded-lg border border-border p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Créditos Disponíveis no Plano
                    </h3>
                    
                    {(usageData.credits?.unlimited || usageData.tokens?.unlimited) ? (
                      <div className="text-center py-4">
                        <p className="text-foreground text-lg font-semibold">
                          Uso Ilimitado
                        </p>
                        <p className="text-muted-foreground text-sm mt-2">
                          Você tem acesso ilimitado a créditos
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Créditos do Plano */}
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-muted-foreground">Créditos Utilizados</span>
                            <span className="text-sm font-semibold text-foreground">
                              {formatCredits(usageData.credits?.current_usage || usageData.tokens?.current_usage || 0)} / {formatCredits(usageData.credits?.base_limit || usageData.tokens?.base_limit || 0)}
                            </span>
                          </div>
                          <div className="w-full bg-background border border-border rounded-full h-4 overflow-hidden">
                            <div
                              className={`h-4 rounded-full transition-all duration-500 ${
                                (usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0) >= 100
                                  ? 'bg-red-500'
                                  : (usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0) >= 90
                                  ? 'bg-orange-500'
                                  : (usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0) >= 80
                                  ? 'bg-yellow-500'
                                  : 'bg-secondary'
                              }`}
                              style={{
                                width: `${Math.max(Math.min(usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0, 100), 2)}%`
                              }}
                            />
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <p className="text-xs text-muted-foreground">
                              {(usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0) >= 100
                                ? '0 créditos restantes'
                                : (usageData.credits?.remaining || usageData.tokens?.remaining_base) >= 0 
                                  ? `${formatCredits(usageData.credits?.remaining || usageData.tokens?.remaining_base || 0)} créditos restantes`
                                  : 'Limite excedido'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {Math.round(usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0)}% utilizado
                            </p>
                          </div>
                        </div>

                        {/* Alerta quando créditos esgotados */}
                        {(usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0) >= 100 && (
                          <div className="mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg">
                            <div className="flex items-start gap-3">
                              <ExclamationCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-semibold text-red-500 mb-1">
                                  Créditos Esgotados
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Você atingiu o limite de créditos do seu plano. Faça upgrade para continuar usando os agentes.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Informação sobre ciclo de faturamento */}
                        <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
                          <div className="flex items-start gap-3">
                            <span className="text-lg">📅</span>
                            <div>
                              <p className="text-sm font-medium text-foreground mb-1">
                                Ciclo de Faturamento
                              </p>
                              {usageData.credits?.cycle_start && usageData.credits?.cycle_end ? (
                                <p className="text-xs text-muted-foreground">
                                  {formatDate(usageData.credits.cycle_start)} até {formatDate(usageData.credits.cycle_end)}
                                  {usageData.credits.days_until_renewal > 0 && (
                                    <span className="ml-2 text-secondary font-medium">
                                      ({usageData.credits.days_until_renewal} dias para renovação)
                                    </span>
                                  )}
                                </p>
                              ) : (
                                <p className="text-xs text-muted-foreground">
                                  Seus créditos são renovados automaticamente a cada ciclo de faturamento.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Botão de upgrade se usar mais de 80% */}
                        {(usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0) >= 80 && (
                          <div className="mt-4">
                            <Button
                              onClick={() => setShowPricingModal(true)}
                              variant="hero"
                              className="w-full"
                            >
                              {(usageData.credits?.usage_percent || usageData.tokens?.usage_percent || 0) >= 90 
                                ? 'Créditos Esgotados - Fazer Upgrade'
                                : '📈 Considere fazer upgrade do plano'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border p-6 text-center">
                  <ChartBarIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Não foi possível carregar os dados de uso.
                  </p>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-6 w-full">
            <section className="space-y-4 w-full">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <CreditCardIcon className="w-5 h-5 text-secondary" />
                Plano Atual
              </h2>

              {subscriptionLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-foreground">Carregando informações da assinatura...</div>
                </div>
              ) : subscription ? (
                <div className="bg-card rounded-lg border border-border p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">
                        {normalizePlanName(
                          subscription.plan_name ?? subscription.plan_config?.plan_name ?? (
                          subscription.subscription_type === 'freemium' ? 'Freemium' :
                          subscription.subscription_type === 'intermediario' ? 'Intermediário' :
                          subscription.subscription_type === 'avancado' ? 'Avançado' :
                          subscription.subscription_type
                        )
                        )}
                      </h3>
                      <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
                        <p className="text-muted-foreground">
                          {subscription.active ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              {getSubscriptionInterval(subscription)} Ativo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              {getSubscriptionInterval(subscription)} Inativo
                            </span>
                          )}
                        </p>
                        {(subscription.expires_at || subscription.renewal_date) && (
                          <p className="text-sm text-muted-foreground">
                            - Sua assinatura atual vence dia {new Date(subscription.expires_at || subscription.renewal_date).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="hero"
                            size="sm"
                            className="w-full sm:w-auto min-w-[180px] justify-between"
                          >
                            Mudar meu plano
                            <ChevronDownIcon className="w-4 h-4 ml-2" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                          <DropdownMenuItem onClick={() => { setPricingModalTitle('Mudar meu plano'); setShowPricingModal(true); }}>
                            Alterar plano atual
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={openCancelModal}>
                            Cancelar assinatura
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={openDeleteModal} className="text-destructive focus:text-destructive">
                            Deletar conta
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {cancelError && (
                    <p className="text-sm text-destructive mt-2">{cancelError}</p>
                  )}

                  {subscription.plan_config && (
                    <div className="mt-6 pt-6 border-t border-border">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Créditos por Ciclo</p>
                        <p className="text-lg font-semibold text-foreground">
                          {subscription.effective_credits ? 
                            new Intl.NumberFormat('pt-BR').format(subscription.effective_credits) : 
                            (subscription.plan_config.base_credits ? 
                              new Intl.NumberFormat('pt-BR').format(subscription.plan_config.base_credits) : 
                              (subscription.plan_config.base_tokens ? 
                                new Intl.NumberFormat('pt-BR').format(subscription.plan_config.base_tokens) : 
                                'Ilimitado'))}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 pt-6 border-t border-border space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Criado em:</span>
                      <span className="text-foreground">
                        {subscription.created_at ? 
                          new Date(subscription.created_at).toLocaleDateString('pt-BR') : 
                          'N/A'}
                      </span>
                    </div>
                    {subscription.expires_at && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Expira em:</span>
                        <span className="text-foreground">
                          {new Date(subscription.expires_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Origem:</span>
                      <span className="text-foreground capitalize">
                        {subscription.source === 'user_registration' ? 'Registro de Usuário' :
                         subscription.source === 'stripe_payment' ? 'Pagamento Stripe' :
                         subscription.source === 'admin_manual' ? 'Criação Manual (Admin)' :
                         subscription.source === 'poc_trial' ? 'POC/Teste' :
                         subscription.source === 'investor_account' ? 'Conta de Investidor' :
                         subscription.source === 'beta_tester' ? 'Beta Tester' :
                         subscription.source === 'partner_account' ? 'Conta de Parceiro' :
                         subscription.source === 'internal_team' ? 'Equipe Interna' :
                         subscription.source}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border p-6 text-center">
                  <CreditCardIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Nenhuma assinatura encontrada.
                  </p>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Pricing Modal */}
        <PricingModal
          isOpen={showPricingModal}
          onClose={() => { setShowPricingModal(false); setPricingModalTitle(''); }}
          onSelectPlan={() => {}}
          title={pricingModalTitle || undefined}
        />

        {/* Modal de Cancelamento (duas etapas) */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent className="sm:max-w-md">
            {cancelModalStep === 1 ? (
              <>
                <DialogHeader>
                  <DialogTitle>Sentimos muito que esteja deixando nossos serviços</DialogTitle>
                  <DialogDescription>
                    Gostaríamos de saber o porque, pode marcar o motivo abaixo?
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-3">
                    {CANCELLATION_REASONS.map((r) => (
                      <label key={r.id} className="flex items-center gap-3 cursor-pointer">
                        <Checkbox
                          checked={cancelReasons[r.id] ?? false}
                          onCheckedChange={(checked) => setCancelReasons((prev) => ({ ...prev, [r.id]: !!checked }))}
                        />
                        <span className="text-sm text-foreground">{r.label}</span>
                      </label>
                    ))}
                  </div>
                  {(cancelReasons.other ?? false) && (
                    <div>
                      <label className="text-sm text-muted-foreground block mb-2">Descreva o motivo:</label>
                      <textarea
                        value={cancelOtherText}
                        onChange={(e) => setCancelOtherText(e.target.value)}
                        className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm"
                        placeholder="Digite aqui..."
                      />
                    </div>
                  )}
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setShowCancelModal(false)} className="w-full sm:w-auto">
                    Não vou cancelar
                  </Button>
                  <Button onClick={() => setCancelModalStep(2)} className="w-full sm:w-auto">
                    Confirmar
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Confirmar cancelamento</DialogTitle>
                  <DialogDescription>
                    A validade atual de sua assinatura é até {(subscription?.expires_at || subscription?.renewal_date) ? new Date(subscription.expires_at || subscription.renewal_date).toLocaleDateString('pt-BR') : '—'}. Se você cancelar agora continuará a ter acesso até esta data e seu plano automaticamente será trocado para o plano gratuito.
                  </DialogDescription>
                </DialogHeader>
                {cancelError ? (
                  <p className="text-sm text-destructive px-1">{cancelError}</p>
                ) : null}
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setShowCancelModal(false)} className="w-full sm:w-auto">
                    Não cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="w-full sm:w-auto"
                  >
                    {cancelLoading ? 'Cancelando...' : 'Cancelar plano'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Deletar Conta */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Deletar conta</DialogTitle>
              <DialogDescription>
                Ao deletar sua conta você perderá todos seus acessos e créditos que possue de forma permanente.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Fechar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deletando...' : 'Confirmar deletar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de aviso de uso elevado (85% a 99%) */}
        {showUsageWarningModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowUsageWarningModal(false)}
          >
            <div 
              className="bg-card rounded-lg shadow-2xl p-8 w-full max-w-md border-2 border-orange-500 transform transition-all duration-200 scale-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 p-3 bg-orange-900/30 rounded-full">
                  <ExclamationCircleIcon className="w-16 h-16 text-orange-400" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Uso elevado de créditos
                </h3>
                <p className="text-muted-foreground mb-4">
                  Você já utilizou <span className="font-bold text-orange-400">{Math.round(usageData?.credits?.usage_percent || usageData?.tokens?.usage_percent || 0)}%</span> dos créditos do seu plano.
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Faça seu upgrade ou aguarde um novo ciclo para renovar seus créditos.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <Button
                    onClick={() => {
                      setShowUsageWarningModal(false);
                      setShowPricingModal(true);
                    }}
                    variant="hero"
                    className="flex-1"
                  >
                    Fazer upgrade
                  </Button>
                  <Button
                    onClick={() => setShowUsageWarningModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Entendi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {success && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={() => setSuccess('')}
          >
            <div 
              className="bg-card rounded-lg shadow-2xl p-8 w-full max-w-md border-2 border-green-500 transform transition-all duration-200 scale-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 p-3 bg-green-900/30 rounded-full">
                  <CheckCircleIcon className="w-16 h-16 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Perfil Atualizado!
                </h3>
                <p className="text-muted-foreground mb-6">
                  {success}
                </p>
                <Button
                  onClick={() => setSuccess('')}
                  variant="hero"
                  className="w-full sm:w-auto min-w-[120px]"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
